import { isPaymentGuaranteeType } from '../state/types';
import { PUBLIC_SEARCH_MODE } from '@shared/types/restaurant';
import type { BookingStep, FlowContext } from './types';

/**
 * All booking steps in canonical order with their conditions.
 *
 * The order here defines the navigation sequence. Each step has an
 * `isRequired` function that determines whether it should be shown
 * based on the current flow context (booking state + restaurant config + slot data).
 *
 * Steps that evaluate to `isRequired: false` are skipped entirely
 * — the orchestrator jumps from the previous step to the next required step.
 */
export const BOOKING_STEPS: BookingStep[] = [
  {
    id: 'DINERS',
    path: '/select-diners',
    isRequired: (ctx: FlowContext) => {
      // Skip when rescheduling — party size is pre-set from the original reservation
      if (ctx.isReschedule) return false;
      return true;
    },
  },

  {
    id: 'DATE',
    path: '/select-date',
    isRequired: (ctx: FlowContext) => {
      // Skip when event has exactly 1 date (auto-selected)
      if (ctx.isEventFlow && ctx.eventDateCount === 1) return false;
      return true;
    },
  },

  {
    id: 'SERVICE',
    path: '/select-service',
    isRequired: (ctx: FlowContext) => {
      // Skip when event has exactly 1 service type (auto-selected)
      if (ctx.isEventFlow && ctx.eventServiceCount === 1) return false;
      // Skip if only 1 service type has real availability for the selected date
      // (or 1 static service type when no date selected yet). Auto-select it.
      // Show if 0 (edge case: let the page handle the empty state) or 2+.
      return ctx.availableServiceCount !== 1;
    },
  },

  {
    id: 'EARLY_ACCESS',
    path: '/early-access',
    isRequired: (ctx: FlowContext) => {
      // Show only when the selected date is a request/early-access day
      return ctx.isEarlyAccessFlow && !ctx.isWaitingList;
    },
  },

  {
    id: 'MENU_FILTER',
    path: '/select-menu-filter',
    isRequired: (ctx: FlowContext) => {
      // Skip when marketplace menu is pre-selected
      if (ctx.meitreNetworkMenuId) return false;
      // Skip when menu is pre-selected from landing page
      if (ctx.preSelectedMenuId) return false;
      // Step 3C: show when restaurant allows experience selection AND there are 2+ menus
      return ctx.allowExperienceSelection && ctx.menuCount > 1;
    },
  },

  {
    id: 'AREA_FILTER',
    path: '/select-area-filter',
    isRequired: (ctx: FlowContext) => {
      if (ctx.meitreNetworkMenuId) return false;
      if (ctx.preSelectedMenuId) return false;
      // Flow D: only area-mode restaurants with 2+ published areas get the filter.
      if (ctx.publicSearchMode !== PUBLIC_SEARCH_MODE.AREA) return false;
      return ctx.publicAreas.length > 1;
    },
  },

  {
    id: 'TIME',
    path: '/select-time',
    isRequired: () => true,
  },

  {
    id: 'SIGN_IN',
    path: '/sign-in',
    isRequired: (ctx: FlowContext) => {
      // Show only when the user is NOT authenticated.
      // Once authenticated, this step is skipped automatically.
      return !ctx.isAuthenticated;
    },
  },

  {
    id: 'COMPLETE_PROFILE',
    path: '/complete-profile',
    isRequired: (ctx: FlowContext) => {
      // Show when authenticated user is missing name OR phone.
      // Once both are provided, this step is skipped automatically.
      return ctx.isAuthenticated && (!ctx.hasPhone || !ctx.hasName);
    },
  },

  {
    id: 'WL_FORM',
    path: '/waiting-list',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule,
  },

  {
    id: 'WL_CALENDAR',
    path: '/waiting-list-calendar',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule,
  },

  {
    id: 'WL_ALTERNATIVES',
    path: '/waiting-list-alternatives',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule && ctx.hasWlAlternatives,
  },

  {
    id: 'AREA',
    path: '/select-area',
    isRequired: (ctx: FlowContext) => {
      if (ctx.isWaitingList) return false;
      // Only 'area' mode activates the area step. 'menu_area' is reserved on
      // the backend but not implemented end-to-end — treat it as menu-mode.
      if (ctx.publicSearchMode !== PUBLIC_SEARCH_MODE.AREA) return false;
      // Need a slot selected with at least one area to render meaningfully.
      if (!ctx.hasAvailableAreas) return false;
      // Single bookable area: routing auto-assigns, skip the step.
      if (ctx.areaAutoAssigned) return false;
      return true;
    },
  },

  {
    id: 'EXPERIENCE',
    path: '/select-experience',
    isRequired: (ctx: FlowContext) => {
      if (ctx.isWaitingList) return false;
      // Menu pre-selected from landing page — no need to choose again
      if (ctx.preSelectedMenuId) return false;
      // Restaurant forces showing the menu step regardless of auto-skip conditions
      if (ctx.forceShowMenuSelection) return true;
      // 2+ menus: always show (user must pick)
      if (ctx.selectedSlotMenus.length >= 2) return true;
      // 1 menu with price or guarantee: show (user sees the info)
      if (ctx.singleMenuHasGuarantee) return true;
      if (ctx.singleMenuHasPrice) return true;
      // Single event menu: always show so user sees event info
      if (ctx.singleMenuIsEvent) return true;
      // Special day (event): always show so user sees event info
      if (ctx.isSpecialDay) return true;
      return false;
    },
  },

  {
    id: 'MARIDAJE',
    path: '/maridaje',
    isRequired: (ctx: FlowContext) => {
      return ctx.hasPairings && ctx.selectedMenuAllowsPairings;
    },
  },

  {
    id: 'PERSONALIZE',
    path: '/personalize',
    isRequired: (ctx: FlowContext) => {
      return ctx.hasPersonalizationOptions;
    },
  },

  {
    id: 'AGENT_GUEST_INFO',
    path: '/agent-guest-info',
    isRequired: (ctx: FlowContext) => ctx.isAgent,
  },

  {
    id: 'GUARANTEE',
    path: '/payment',
    isRequired: (ctx: FlowContext) => {
      // Show if guarantee type is deposit or no_show AND fee > 0
      // AND user hasn't already provided a card token.
      // Agent bypass: agents skip payment entirely.
      if (ctx.isAgent) return false;
      if (ctx.hasCardToken) return false;
      if (ctx.guaranteeFee <= 0) return false;

      return isPaymentGuaranteeType(ctx.selectedSlotType);
    },
  },

  {
    id: 'CONFIRMATION',
    path: '/confirmation',
    /**
     * Phase 2 of Confirm-step removal: drop the dedicated confirmation page
     * when the per-tenant `bookingSkipConfirmationV2` flag is on. The flag is
     * frozen at session start by `BookingProvider` (T11) and surfaced as
     * `ctx.skipConfirmStep`, so the step set is stable for the lifetime of
     * the session. When CONFIRMATION drops, the previous step (Personalize
     * if enabled, else Guarantee, else the appropriate fallback) becomes the
     * commit host — `useCommitOrAdvance` dispatches on "next step is
     * BOOKING_FINAL" rather than the flag directly.
     */
    isRequired: (ctx: FlowContext) => !ctx.skipConfirmStep,
  },

  {
    id: 'BOOKING_FINAL',
    path: '/booking-final',
    isRequired: () => true,
  },
];

/**
 * Get the list of active steps for a given context.
 */
export function getActiveSteps(ctx: FlowContext): BookingStep[] {
  return BOOKING_STEPS.filter((step) => step.isRequired(ctx));
}

/**
 * Determine whether a guarantee is required based on type and fee.
 * Exported for use outside the flow (e.g., confirmation page logic).
 */
export function isPaymentGuarantee(
  type: string | null | undefined,
  fee: number | null | undefined,
): boolean {
  return isPaymentGuaranteeType(type) && (fee ?? 0) > 0;
}

/**
 * Map a URL pathname to its canonical booking step id, if it matches one.
 * Returns null for paths outside the booking flow.
 */
export function getStepIdFromPath(pathname: string): string | null {
  const match = BOOKING_STEPS.find((s) => s.path === pathname);
  return match?.id ?? null;
}
